import { ref } from 'vue';

// This project has no .env/VUE_APP_* mechanism anywhere (confirmed — every
// other cross-app URL, e.g. main.js's api_base_url, is a hardcoded
// production default + a localStorage override for local dev, where neither
// app's dev server has a fixed port). Same key PreviewPanel's host component
// (index.vue) reads independently for the external "معاينة الموقع" link —
// duplicated as-is, matching the reference implementation rather than
// centralizing it into shared config.
function resolvePreviewOrigin() {
  return localStorage.getItem('homepage_preview_origin') || 'https://tarekmath.com';
}

/**
 * The builder-side half of the live preview postMessage protocol — ported
 * from the reference implementation's PreviewFrame.vue. Talks to exactly
 * one iframe (passed into `attach`).
 *
 * `ready` starts `false`, flips `true` only on a real `preview-ready`
 * message from the iframe itself, or flips to the string `'error'` if a
 * 6-second fallback timer (started on the iframe's native `load` event)
 * elapses without one ever arriving — this is what distinguishes "still
 * loading" from "the iframe returned 200 but its own router/guard never
 * let the preview component mount," which a bare timeout can't tell apart
 * on its own. `sendDraft`/`sendSelection` are no-ops until ready; there is
 * no queueing of missed sends because both are also independently re-sent
 * once, immediately, as soon as `preview-ready` arrives.
 */
export function useHomepagePreview() {
  const previewOrigin = resolvePreviewOrigin();
  const ready = ref(false);

  let iframeEl = null;
  let readyTimeout = null;
  let latestSections = [];
  let latestSelectedKey = null;

  function clearReadyTimeout() {
    if (readyTimeout) {
      clearTimeout(readyTimeout);
      readyTimeout = null;
    }
  }

  function sendDraft() {
    if (ready.value !== true) return;
    iframeEl?.contentWindow?.postMessage(
      { source: 'homepage-builder', type: 'draft-update', payload: { sections: latestSections } },
      previewOrigin
    );
  }

  function sendSelection() {
    if (ready.value !== true) return;
    iframeEl?.contentWindow?.postMessage(
      { source: 'homepage-builder', type: 'select-section', payload: { key: latestSelectedKey } },
      previewOrigin
    );
  }

  function handleMessage(event) {
    if (event.origin !== new URL(previewOrigin).origin) return;
    const msg = event.data;
    if (!msg || msg.source !== 'homepage-builder') return;

    if (msg.type === 'preview-ready') {
      ready.value = true;
      clearReadyTimeout();
      sendDraft();
      sendSelection();
    } else if (msg.type === 'section-click') {
      onSectionClick?.(msg.payload?.key ?? null);
    }
  }

  let onSectionClick = null;
  function setSectionClickHandler(fn) {
    onSectionClick = fn;
  }

  // Called on the iframe's native `load` event. Starts the 6s fallback
  // timer — but never downgrades `ready` back to loading/error if the
  // preview-ready handshake already completed before `load` fired (that
  // ordering can happen, and would otherwise flash a false error state).
  function onFrameLoad() {
    clearReadyTimeout();
    if (ready.value === true) return;
    readyTimeout = setTimeout(() => {
      if (ready.value !== true) ready.value = 'error';
    }, 6000);
  }

  function updateSections(sections) {
    latestSections = sections;
    sendDraft();
  }

  function updateSelection(key) {
    latestSelectedKey = key;
    sendSelection();
  }

  // Called once when the iframe element mounts (and again if the preview
  // is reloaded, e.g. switching Mobile/Desktop re-attaches to a fresh
  // iframe with its own new handshake).
  function attach(el) {
    iframeEl = el;
    ready.value = false;
    clearReadyTimeout();
  }

  window.addEventListener('message', handleMessage);
  function dispose() {
    clearReadyTimeout();
    window.removeEventListener('message', handleMessage);
  }

  return {
    previewOrigin,
    ready,
    attach,
    onFrameLoad,
    updateSections,
    updateSelection,
    setSectionClickHandler,
    dispose,
  };
}
